import React from 'react';
import { Eye, Brain, Sparkles, ArrowRight, Activity, Shield, Cpu, Zap, Network, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ProductsSection = () => {
  const { t } = useLanguage();

  const owlFeatureIcons = [Activity, Shield, Cpu];
  const icornetFeatureIcons = [BarChart3, Network, Zap];

  return (
    <section id="products" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-amber-700 font-medium mb-4 block">{t.products.subtitle}</span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">{t.products.title}</h2>
        </div>

        {/* Products Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* OWL Product */}
          <div className="group relative bg-white rounded-3xl overflow-hidden border border-gray-200 hover:border-amber-300 shadow-sm hover:shadow-xl transition-all duration-500">
            {/* Product Header */}
            <div className="relative h-48 bg-gradient-to-br from-amber-600 to-amber-800 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.1),transparent)]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Eye className="w-20 h-20 text-white/90 group-hover:scale-110 transition-transform duration-500" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <Badge variant="secondary" className="bg-amber-100 text-amber-700 mb-4">
                {t.products.owl.tag}
              </Badge>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">{t.products.owl.title}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t.products.owl.description}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {t.products.owl.features.map((feature, index) => {
                  const Icon = owlFeatureIcons[index];
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-amber-700" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  );
                })}
              </div>

              {/*
              <Button className="w-full bg-amber-700 hover:bg-amber-800 text-white group/btn">
                {t.products.owl.cta}
                <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button> */}
            </div>
          </div>

          {/* ICorNet Product */}
          <div className="group relative bg-white rounded-3xl overflow-hidden border border-gray-200 hover:border-gray-400 shadow-sm hover:shadow-xl transition-all duration-500">
            {/* Product Header */}
            <div className="relative h-48 bg-gradient-to-br from-gray-700 to-gray-900 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent)]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <Brain className="w-20 h-20 text-white/90 group-hover:scale-110 transition-transform duration-500" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="secondary" className="bg-gray-200 text-gray-700">
                  {t.products.icornet.tag}
                </Badge>
                <Badge className="bg-amber-600 text-white border-0">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t.products.icornet.tagNew}
                </Badge>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4">{t.products.icornet.title}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t.products.icornet.description}
              </p>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {t.products.icornet.features.map((feature, index) => {
                  const Icon = icornetFeatureIcons[index];
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-gray-700" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  );
                })}
              </div>
              
              {/*
              <Button className="w-full bg-gray-800 hover:bg-gray-900 text-white group/btn">
                {t.products.icornet.cta}
                <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </Button> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;